import { inject } from "@angular/core";
import { Auth } from "@angular/fire/auth";
import { CanActivateFn, Router, UrlTree } from "@angular/router";
import { PermissionsService } from "../services/permissions.service";

/**
 * Sends an admin who belongs to exactly one organization straight into it,
 * rather than through an overview built for people who manage several.
 *
 * Anyone else — global admins, admins of more than one organization, and users
 * with no access at all — sees the overview. The last case is deliberate: the
 * page can explain the situation, which is more useful than a redirect loop.
 */
export const landingGuard: CanActivateFn = async (): Promise<boolean | UrlTree> => {
	const permissions = inject(PermissionsService);
	const router = inject(Router);
	const auth = inject(Auth);

	const user = auth.currentUser;
	if (!user) {
		return true;
	}

	if (await permissions.isGlobalAdmin(user.uid)) {
		return true;
	}

	const access = await permissions.getAllOrganizationAccess(user.uid);
	if (access.length !== 1) {
		return true;
	}

	const organizationId = access[0].data?.["organization_id"] as string | undefined;
	if (!organizationId) {
		return true;
	}

	return router.createUrlTree(["/organizations", organizationId, "dashboard"]);
};
